import { DataTable } from '@/components/DataTable';
import { OneLineText } from '@/components/OneLineText';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { displayError } from '@/lib/error';
import { downloadFilefromBuffer, formatPhoneNumber } from '@/lib/utils';
import { getClients } from '@/services/Network';
import { ClientGetAllParams, ClientResponse, useClientCsvPost } from '@brainvaulttech/dgr-react-sdk';
import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';

export const ExportClientDialog = ({ onClose }: { onClose: () => void }) => {
  const [data, setData] = useState<ClientResponse[]>([]);
  const { mutate: getDownloadUrl, status } = useClientCsvPost();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getAllClients = (params: ClientGetAllParams, options?: any) => {
    const updatedParams = { ...params, pageSize: 1000 };
    return getClients(updatedParams, options);
  };

  const handleDownloadCSV = () => {
    const today = new Date();
    const fileName = `Client_Master_List_${today.getFullYear()}${(today.getMonth() + 1)
      .toString()
      .padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}`;
    getDownloadUrl(void 0, {
      onSuccess: (data) => {
        downloadFilefromBuffer(data.data.downloadUrl, fileName, 'CSV');
      },
      onError: (e) => {
        displayError(e, 5000);
      },
    });
  };

  const columns: ColumnDef<ClientResponse>[] = [
    {
      accessorKey: 'code',
      header: 'Client ID',
      cell: ({ cell }) => {
        const code = cell.getValue() as number;
        return <div className={`w-full h-full flex flex-col min-w-[100px]`}>{code}</div>;
      },
    },
    {
      accessorKey: 'lastName',
      header: 'Client Name',
      cell: ({ row }) => {
        return <OneLineText text={row.original.clientName ?? ''} className="min-w-[150px] mr-5" />;
      },
    },
    {
      accessorKey: 'address1',
      header: 'Address',
      size: 200,
      cell: ({ row }) => {
        return (
          <OneLineText
            text={row.original.address1 + (row.original.address2 ? ', ' + row.original.address2 : '')}
            className="min-w-[150px] mr-5"
          />
        );
      },
    },
    {
      accessorKey: 'city',
      header: 'City',
      cell: ({ cell }) => {
        return <OneLineText text={cell.getValue() as string} className="min-w-[100px] mr-5" />;
      },
    },
    {
      accessorKey: 'state',
      header: 'State',
      cell: ({ cell }) => {
        return <div className="min-w-[100px]">{cell.getValue() as string}</div>;
      },
    },
    {
      accessorKey: 'zipCode',
      header: 'Zip Code',
      cell: ({ cell }) => {
        return <div className="min-w-[100px]">{cell.getValue() as string}</div>;
      },
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ cell }) => {
        const phone = cell.getValue() as string;
        return <div className="min-w-[150px]">{phone ? formatPhoneNumber(phone) : '-'}</div>;
      },
    },
  ];
  return (
    <Sheet modal={true} open={true}>
      <SheetContent
        hideCloseButton={true}
        className="md:w-[60vw] w-[90vw] md:gap-12 md:max-h-[80vh] max-h-[100vh] h-fit flex flex-col scrollbar-hide focus:outline-none rounded-lg gap-4 p-8"
        tabIndex={-1}
        side={'center'}
      >
        <div className="flex-grow min-h-0 gap-4 md:gap-8 flex flex-col">
          <SheetTitle>Preview - Client Master List</SheetTitle>
          <DataTable
            columns={columns}
            apiGet={getAllClients}
            showPagination={false}
            stickyHeader
            className="max-h-[44vh] min-h-[10px] h-fit overflow-auto custom-scrollbar"
            setRows={setData}
            showNRecords
            customPageSize={1000}
          />
        </div>
        <div className="w-full flex flex-col gap-4 md:flex-row justify-end ">
          <Button
            type="button"
            variant="outline"
            className="text-primary w-[75px] h-[36px] py-2 px-4 rounded-md focus:outline-none border-primary hover:bg-[#EDF5FF]"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            variant="outline"
            className="text-[#fff] h-[36px] w-fit min-w-[135px] py-2 px-4 rounded-md bg-primary border-primary hover:bg-[#12375C] hover:text-white"
            onClick={handleDownloadCSV}
            disabled={data.length === 0 || status === 'pending'}
            isLoading={status === 'pending'}
          >
            Download CSV
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
